import React, { useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import CaseViewer from "@/components/CaseViewer";
import { DevicePanelRail } from "@/components/devicePanel/DevicePanelRail";
import { stepInStudy, type ReviewModel } from "@/components/devicePanel/Review";
import { ReviewSwitch, taskLabel, usePanelReview } from "@/components/devicePanel/ReviewSwitch";
import { StepButtons, StepCounter, useCaseSteps } from "@/components/devicePanel/CaseSteps";
import { PanelCaseSkeleton, PanelRailSkeleton } from "@/components/devicePanel/PanelSkeleton";
import { CASE_HEADER, CASE_STEP_BUTTON } from "@/components/devicePanel/panelLayout";
import { PanelLoadError } from "@/components/devicePanel/PanelLoadError";
import { ResearchTag } from "@/components/devicePanel/ResearchTag";
import { NotificationPage } from "@/components/devicePanel/NotificationPage";
import { useAuth } from "@/context/AuthContext";
import {
  panelImagesUrl,
  useDevicePanelStudy,
  useReviewProgress,
  useSaveVerdict,
  useStudyReview,
} from "@/hooks/useDevicePanel";
import {
  firstFlagged,
  flaggedKeySlices,
  isListed,
  panelCaseUrl,
  panelUrl,
} from "@/types/devicePanel";
import {
  nextToReview,
  parseAtParam,
  parseFindingParam,
  previousInQueue,
  reviewUrl,
  roundLabel,
} from "@/types/devicePanelReview";

/** One study opened from a device panel: the images, with every device's output in
 * the rail. View (the default) shows the output alone. A reader with a review
 * task gets a switch to Review, which puts the round's verdict line on the rows
 * and adds the study's progress and Previous / Next (k / j on a review row)
 * through the round's queue: Next to the next unjudged item, Previous to the
 * previous item, in rail order within a study and in queue order across studies.
 * In View, Prev / Next (← / →) step through the panel's studies in worklist
 * order, the neighbours loaded ahead. It opens on the top triage finding when it reads Flagged, at its key
 * slice (else on the viewer's default hanging protocol at the first slice);
 * flagged key slices are ticked on the slice bars.
 * The images are read through the panel, never through a device's job.
 * `?finding=<device>:<condition>` opens on that finding instead (in View, only a
 * finding the panel lists), and, in Review,
 * `?at=next` / `?at=last` on the study's first unjudged / last queued row: the
 * row scrolls into view, takes focus and jumps to its key slice. `?mode=` and
 * `?round=` pick the mode and the round. A study of a panel in the notification
 * view opens as its notification page instead, with no viewer. */
const DevicePanelCasePage: React.FC = () => {
  const { panelId, studyUid } = useParams<{ panelId: string; studyUid: string }>();
  const [params] = useSearchParams();
  const findingValue = params.get("finding");
  const link = findingValue === null ? null : parseFindingParam(findingValue);
  const atValue = params.get("at");
  const at = atValue === null ? null : parseAtParam(atValue);
  const navigate = useNavigate();
  // The study whose round was found fully judged by a Next with nowhere to go.
  const [allJudgedAt, setAllJudgedAt] = useState<string | null>(null);
  const { token } = useAuth();
  const { data, isLoading, error } = useDevicePanelStudy(token, panelId, studyUid);
  const keySlices = useMemo(() => (data ? flaggedKeySlices(data.devices) : []), [data]);
  const progress = {
    blind: useReviewProgress(token, panelId, "blind"),
    report_check: useReviewProgress(token, panelId, "report_check"),
  };
  const { task, round, setMode, unknown } = usePanelReview(panelId!, {
    blind: progress.blind.data,
    report_check: progress.report_check.data,
  });
  // View steps through every study; Review steps through its round's queue.
  const steps = useCaseSteps(panelId!, studyUid!, {
    enabled: !round && data?.view === "research",
  });
  const studyReview = useStudyReview(token, panelId, studyUid, !!round);
  const saveVerdict = useSaveVerdict(token, panelId!, studyUid!);
  // A notification-view study opens as its notification page; "Open study" on a
  // panel whose target is the viewer (`?read`) reads it here in full instead.
  const reading = data?.view === "notification" && params.has("read");
  if (data?.view === "notification" && !reading)
    return <NotificationPage key={data.study_uid} study={data} />;
  // The queues decide the review switch, so the header waits for them too.
  if (isLoading || progress.blind.isLoading || progress.report_check.isLoading)
    return <PanelCaseSkeleton />;
  if (error || !data) return <PanelLoadError what="study" error={error} />;
  if (unknown)
    return (
      <p role="alert" className="p-6 text-sm text-fg-muted">
        This link names an unknown {unknown}.
      </p>
    );

  const queue = (round && progress[round].data?.studies) || [];
  const inQueue = queue.find((study) => study.study_uid === data.study_uid);
  const step = (direction: 1 | -1) => {
    if (!round || stepInStudy(direction)) return;
    if (direction === -1) {
      const before = previousInQueue(queue, data.study_uid);
      if (before) navigate(reviewUrl(data.panel_id, before.study_uid, round, "last"));
      return;
    }
    const after = nextToReview(queue, data.study_uid);
    if (after) navigate(reviewUrl(data.panel_id, after.study_uid, round, "next"));
    else if (!stepInStudy(1, true)) setAllJudgedAt(data.study_uid);
  };
  const review: ReviewModel | null =
    round && studyReview.data
      ? { data: studyReview.data, round, save: saveVerdict.mutateAsync, step }
      : null;
  const linkProblem =
    // A row position only means something in Review; in View it is ignored.
    atValue !== null && round && !at
      ? "This link names an unknown position."
      : findingValue === null
        ? null
        : !link
          ? "This link does not name a finding."
          : // In View a link opens only a row the panel lists; in Review it also
            // reaches a call the round holds open.
            !(round
                ? data.devices.some(
                    (device) =>
                      device.key === link.device &&
                      device.findings?.some((finding) => finding.condition === link.condition),
                  )
                : isListed(data.devices, link))
            ? "The linked finding is not on this study."
            : null;
  return (
    <div className="flex h-full flex-col">
      <div className={CASE_HEADER}>
        <Link
          to={reading ? panelCaseUrl(data.panel_id, data.study_uid) : panelUrl(data.panel_id)}
          className="shrink-0 text-fg-muted hover:text-fg"
        >
          {reading ? "← Back to notification" : `← ${data.name}`}
        </Link>
        <span className="font-medium text-fg">{data.label}</span>
        {reading ? (
          data.research_label && (
            <span className="text-[11px] text-fg-muted">{data.research_label}</span>
          )
        ) : (
          <ResearchTag label={data.research_label} />
        )}
        <span className="text-[11px] text-fg-muted">{data.region}</span>
        {studyReview.isError && (
          <span role="alert" className="text-[11px] text-warn">
            Review could not load
          </span>
        )}
        {linkProblem && (
          <span role="alert" className="text-[11px] text-warn">
            {linkProblem}
          </span>
        )}
        {/* The switch stays at the right edge; Review's progress and steps open
            to its left. */}
        <span className="ml-auto flex items-center gap-3">
          {steps && (
            <>
              <StepCounter steps={steps} noun="case" />
              <span role="group" aria-label="Cases" className="flex gap-1">
                <StepButtons
                  panelId={data.panel_id}
                  steps={steps}
                  noun="case"
                  className={CASE_STEP_BUTTON}
                />
              </span>
            </>
          )}
          {round && (
            <>
              {inQueue && (
                <span className="font-mono text-[11px] text-fg-dim tabular-nums">
                  {inQueue.reviewed_items}/{inQueue.items} reviewed here
                </span>
              )}
              {allJudgedAt === data.study_uid && (
                <span role="status" className="text-[11px] text-fg-muted">
                  Every item in the {roundLabel(round).toLowerCase()} is judged
                </span>
              )}
              <span role="group" aria-label="Review queue" className="flex gap-1">
                {(
                  [
                    [-1, "‹ Previous", "k"],
                    [1, "Next ›", "j"],
                  ] as const
                ).map(([direction, label, key]) => (
                  <button
                    key={key}
                    type="button"
                    aria-keyshortcuts={key}
                    disabled={!review}
                    onClick={() => step(direction)}
                    className={CASE_STEP_BUTTON}
                  >
                    {label}
                  </button>
                ))}
              </span>
            </>
          )}
          {task && (
            <ReviewSwitch
              label={taskLabel(task)}
              on={!!round}
              onChange={(on) => setMode(on ? "review" : "view")}
            />
          )}
        </span>
      </div>
      <div className="min-h-0 flex-1">
        <CaseViewer
          key={data.study_uid}
          studyData={{ studyId: data.study_uid }}
          imagesUrl={panelImagesUrl(data.panel_id, data.study_uid)}
          keySlices={keySlices}
          rightRail={(jumpToSlice) =>
            // The rail waits for the review, so its verdict lines never
            // appear under rows already drawn.
            round && studyReview.isLoading ? (
              <PanelRailSkeleton />
            ) : (
              <DevicePanelRail
                study={data}
                review={review}
                link={linkProblem ? null : (link ?? (round ? at : firstFlagged(data.devices)))}
                jumpToSlice={jumpToSlice}
              />
            )
          }
        />
      </div>
    </div>
  );
};

export default DevicePanelCasePage;
